import { useLocation } from "react-router";
import useSWRInfinite from "swr/infinite";
import { z } from "zod";
import { PaginationSchema } from "@/ui/pagination/Pagination";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import { useInstagramSearch } from "./useInstagramSearch";
import { useIsInstagramSearchInfluencers } from "./useInstagramSearchRouter";

const InstagramSearchProfilesResponseSchema = z.object({
  count: z.number(),
  totalCount: z.number(),
  profiles: InstagramSearch.ProfileSchema.array(),
  pagination: PaginationSchema,
});

export type InstagramSearchProfilesResponse = z.infer<
  typeof InstagramSearchProfilesResponseSchema
>;

export const useInstagramSearchProfiles = () => {
  const config = useInstagramSearch((state) => state.config);
  const id = useInstagramSearch((state) => state.id);
  const query = useInstagramSearch((state) => state.query);
  const category = useInstagramSearch((state) => state.category);
  const country = useInstagramSearch((state) => state.country);
  const regions = useInstagramSearch((state) => state.regions);
  const cities = useInstagramSearch((state) => state.cities);
  const gender = useInstagramSearch((state) => state.gender);
  const followers = useInstagramSearch((state) => state.followers);
  const posts = useInstagramSearch((state) => state.posts);
  const influencersCategory = useInstagramSearch(
    (state) => state.influencersCategory,
  );
  const sortingInfluencers = useInstagramSearch(
    (state) => state.sortingInfluencers,
  );
  const sortingProfiles = useInstagramSearch((state) => state.sortingProfiles);
  const isAuthorized = useInstagramSearch((state) => state.isAuthorized);
  const isSubscribed = useInstagramSearch((state) => state.isSubscribed);
  const isInfluencers = useIsInstagramSearchInfluencers();
  const { pathname } = useLocation();

  const response = useSWRInfinite(
    (index, data: InstagramSearchProfilesResponse | null) => {
      if (
        data !== null &&
        data.pagination.currentPage === data.pagination.totalPages
      ) {
        return null;
      }

      if (isInfluencers) {
        return [
          config.api.getInfluencers,
          index,
          pathname,
          id,
          influencersCategory,
          sortingInfluencers,
          isAuthorized,
          isSubscribed,
        ];
      }

      return [
        config.api.getProfiles,
        index,
        pathname,
        id,
        query,
        category,
        country,
        regions,
        cities,
        gender,
        followers,
        posts,
        sortingProfiles,
        isAuthorized,
        isSubscribed,
      ];
    },
    async ([api, index]) => {
      const page = index + 1;

      if (isInfluencers) {
        const { data } = await Network.get<InstagramSearchProfilesResponse>(
          api,
          {
            category: influencersCategory?.id,
            sorting: sortingInfluencers?.id,
            page,
          },
        );

        return zodUnsafeValidate(InstagramSearchProfilesResponseSchema, data);
      } else {
        const { data } = await Network.get<InstagramSearchProfilesResponse>(
          api,
          {
            query,
            category: category,
            country: country,
            regions: Array.from(regions),
            cities: Array.from(cities),
            gender: gender,
            followersStart: followers.start,
            followersEnd: followers.end,
            postsStart: posts.start,
            postsEnd: posts.end,
            sortingProfiles: sortingProfiles?.id,
            page,
          },
        );

        return zodUnsafeValidate(InstagramSearchProfilesResponseSchema, data);
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      shouldRetryOnError: false,
      keepPreviousData: true,
    },
  );

  const lastPage = response.data?.[response.data.length - 1];

  const pagination = lastPage?.pagination;

  const isPaginationAvailable =
    pagination !== undefined && pagination.currentPage < pagination.totalPages;

  return {
    ...response,
    isPaginationAvailable,
  };
};
